import { faVideoSlash } from '@fortawesome/pro-solid-svg-icons';
import { FC } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import {
  AccessibleSelect,
  Checkbox,
  IconBadge,
  InputControl,
  MenuDivider,
  SelectOptionAtom,
  cn,
} from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks/useTheme';
import { LocalAudioVideoThumbnailProps } from '../../session';
import { NoAccessVariant } from '../../session/controls';
import { Theme } from '../../state/theme';
import { WithPassThroughProps } from './BlockedPermissionMessage';
import { VideoQualitySettings, VideoQualitySettingsProps } from './VideoQualityOptions';

export interface VideoSettingsTabProps extends VideoQualitySettingsProps {
  /** Shown when access is blocked. The wrapper passes the app wrapper; stories and tests pass the pure message. */
  blockedPermissionMessageComponent: FC<WithPassThroughProps>;
  cameraOptions: SelectOptionAtom[];
  setCamera: (selectedId: string) => void;
  selectedCamera?: string;
  thumbnailComponent: React.FC<Omit<LocalAudioVideoThumbnailProps, 'name'>>;
  videoIsBlocked: boolean;
  videoIsLoading?: boolean;
  isMirrored: boolean;
  setIsMirrored: (isMirrored: boolean) => void;
  /**
   * Whether to show the video quality settings section. This section was built
   * out to support Twilio Video quality optimizations and should be hidden when
   * Twilio Video is not in use.
   */
  shouldShowVideoQualitySettings?: boolean;
}

export function VideoSettingsTab({
  blockedPermissionMessageComponent: BlockedPermissionMessage,
  cameraOptions,
  setCamera,
  selectedCamera,
  thumbnailComponent: LocalAudioVideoThumbnail,
  videoIsBlocked,
  videoIsLoading,
  isMirrored,
  setIsMirrored,
  qualityMode,
  onChangeQualityMode,
  shouldShowAutoOptimizationAlert,
  storedQualityMode,
  shouldShowVideoQualitySettings,
}: VideoSettingsTabProps) {
  const t = useTranslate();
  const { theme } = useTheme();

  const isDarkTheme = theme === Theme.DARK;
  const shouldDisableFields = videoIsBlocked || videoIsLoading;

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex justify-center'>
        <LocalAudioVideoThumbnail
          videoClassName='max-h-none max-w-none w-full h-[216px]'
          className='w-full h-full'
          showExpandVideoToggle={false}
          isSettingsThumbnail={true}
          isSpeaking={false}
          shouldShowHideSelf={false}
          videoOffContent={
            <IconBadge size='xl' className='bg-neutral-800' iconClassName='text-coral-600' icon={faVideoSlash} />
          }
        />
      </div>
      <InputControl
        label={t('telehealth-core:settings.camera')}
        disabled={shouldDisableFields}
        className='mb-0'
        labelClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      >
        <AccessibleSelect
          onChange={(value: string) => setCamera(value)}
          options={cameraOptions}
          value={selectedCamera ?? cameraOptions[0]?.value}
          placeholder={videoIsBlocked ? t('telehealth-core:settings.noAccess') : t('telehealth-core:settings.select')}
        />
      </InputControl>
      <Checkbox
        label={t('telehealth-core:settings.mirrorMyVideo')}
        checked={isMirrored}
        onChange={() => setIsMirrored(!isMirrored)}
        disabled={shouldDisableFields}
        iconClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
        textClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      />
      {videoIsBlocked && <BlockedPermissionMessage noAccessVariant={NoAccessVariant.Camera} />}
      {shouldShowVideoQualitySettings && (
        <>
          <MenuDivider />
          <VideoQualitySettings
            qualityMode={qualityMode}
            onChangeQualityMode={onChangeQualityMode}
            shouldDisableFields={shouldDisableFields}
            shouldShowAutoOptimizationAlert={shouldShowAutoOptimizationAlert}
            storedQualityMode={storedQualityMode}
          />
        </>
      )}
    </div>
  );
}
